import { useCallback, useEffect, useRef, useState } from 'react';

import CONFIG from '../config';
import TopHeaderMenu from '../components/TopHeaderMenu';
import CompeticionHeaderMenu from '../components/CompeticionHeaderMenu';
import { useNavigate, useParams } from 'react-router-dom';
import { calculaPrevision } from '../utils/time';
import { applyDelta, enrichResultados } from '../utils/wsDelta';

const maplibregl = window.maplibregl;

function crearMarkerEl(dorsal, finalizado) {
  const el = document.createElement('div');
  el.style.cssText = `
    background:${finalizado ? '#22c55e' : '#d42c19'};
    color:white;font-size:10px;font-weight:bold;
    border-radius:50%;width:28px;height:28px;
    display:flex;align-items:center;justify-content:center;
    border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.5);
    font-family:monospace;`;
  el.textContent = dorsal;
  return el;
}

function CompeticionGps() {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);
  const intervalRef = useRef(null);

  const [evento, setEvento] = useState(null);
  const [competicion, setCompeticion] = useState(null);
  const [controles, setControles] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [allResultados, setAllResultados] = useState([]);
  const [lineCoords, setLineCoords] = useState([]);

  const stateRef = useRef({ competicion: null, controles: [], sectores: [], allResultados: [], lineCoords: [] });

  const navigate = useNavigate();
  const params = useParams();

  function onClickBack() {
    navigate(`/competicion/${params.competicionId}`);
  }

  // Inicializar mapa
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: 'https://tiles.openfreemap.org/styles/liberty',
      center: [-8.13, 43.0],
      zoom: 13,
      pitch: 0,
      maxPitch: 85,
    });
    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    map.on('load', () => {
      // Terreno 3D
      map.addSource('terrain-dem', {
        type: 'raster-dem',
        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
        encoding: 'terrarium',
        tileSize: 256,
        maxzoom: 15,
      });
      map.setTerrain({ source: 'terrain-dem', exaggeration: 1.5 });

      // Source para la ruta (se rellena después)
      map.addSource('ruta', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'ruta-line',
        type: 'line',
        source: 'ruta',
        paint: {
          'line-color': CONFIG.PRIMARY_COLOR,
          'line-width': 4,
        },
      });
    });
    mapRef.current = map;
    return () => map.remove();
  }, []);

  // Dibujar ruta cuando llegan coords
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !lineCoords.length) return;
    const waitForSource = () => {
      if (!map.getSource('ruta')) { setTimeout(waitForSource, 100); return; }
      map.getSource('ruta').setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: lineCoords },
      });
      // Fit bounds
      const bounds = lineCoords.reduce(
        (b, c) => b.extend(c),
        new maplibregl.LngLatBounds(lineCoords[0], lineCoords[0])
      );
      map.fitBounds(bounds, { padding: 60, pitch: 0, animate: false });

      // Markers salida/meta
      new maplibregl.Marker({ color: '#22c55e' }).setLngLat(lineCoords[0]).addTo(map);
      new maplibregl.Marker({ color: '#ef4444' }).setLngLat(lineCoords[lineCoords.length - 1]).addTo(map);

      // Markers de controles intermedios
      const { controles: ctrl, lineCoords: coords } = stateRef.current;
      if (ctrl.length && coords.length) {
        const ultimoControl = ctrl[ctrl.length - 1];
        let totalDist = 0;
        for (let i = 1; i < coords.length; i++) {
          const dx = (coords[i][0] - coords[i-1][0]) * 111320 * Math.cos(coords[i][1] * Math.PI / 180);
          const dy = (coords[i][1] - coords[i-1][1]) * 110540;
          totalDist += Math.sqrt(dx*dx + dy*dy);
        }
        ctrl.forEach(c => {
          if (c.tipo === 'META' || c.tipo === 'SALIDA') return;
          const fraccion = c.distancia / ultimoControl.distancia;
          const targetDist = fraccion * totalDist;
          let acum = 0, lng = coords[0][0], lat = coords[0][1];
          for (let i = 1; i < coords.length; i++) {
            const dx2 = (coords[i][0] - coords[i-1][0]) * 111320 * Math.cos(coords[i][1] * Math.PI / 180);
            const dy2 = (coords[i][1] - coords[i-1][1]) * 110540;
            const seg = Math.sqrt(dx2*dx2 + dy2*dy2);
            if (acum + seg >= targetDist) {
              const t = (targetDist - acum) / seg;
              lng = coords[i-1][0] + t * (coords[i][0] - coords[i-1][0]);
              lat = coords[i-1][1] + t * (coords[i][1] - coords[i-1][1]);
              break;
            }
            acum += seg;
          }
          const el = document.createElement('div');
          el.innerHTML = '<img src="/img/ico-control-v2.png" style="width:22px;height:35px" />';
          new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat([lng, lat]).addTo(map);
        });
      }
    };
    waitForSource();
  }, [lineCoords]);

  // Calcular posiciones
  const calcularPosiciones = useCallback(() => {
    const { competicion: comp, controles: ctrl, sectores: sect, allResultados: results, lineCoords: coords } = stateRef.current;
    if (!comp || !ctrl.length || !coords.length || !results.length) return;
    const map = mapRef.current;
    if (!map) return;

    const ultimoControl = ctrl[ctrl.length - 1];
    if (!ultimoControl || !ultimoControl.distancia) return;

    // Distancia total de la ruta en metros (aprox)
    let totalDist = 0;
    for (let i = 1; i < coords.length; i++) {
      const dx = (coords[i][0] - coords[i-1][0]) * 111320 * Math.cos(coords[i][1] * Math.PI / 180);
      const dy = (coords[i][1] - coords[i-1][1]) * 110540;
      totalDist += Math.sqrt(dx*dx + dy*dy);
    }

    const dorsalesActivos = new Set();
    for (const resultado of results) {
      const prev = calculaPrevision(comp, ctrl, sect, resultado);
      const metaControlId = ultimoControl.id;
      const finalizado = !!resultado['control_' + metaControlId + '_puesto'];

      let distMetros = 0;
      if (finalizado) {
        // Finalizado → en META
        distMetros = ultimoControl.distancia;
      } else if (prev && prev.distancia !== null && prev.inicioDistancia !== null) {
        distMetros = prev.inicioDistancia + prev.distancia;
      } else if (resultado.control_ultimo_id) {
        // Con salida pero sin previsión → en SALIDA
        const salidaCtrl = ctrl.find(c => c.tipo === 'SALIDA');
        distMetros = salidaCtrl ? salidaCtrl.distancia : 0;
      } else {
        // Sin salida → no mostrar
        continue;
      }
      if (distMetros < 0) continue;

      const fraccion = Math.min(distMetros / ultimoControl.distancia, 1);
      // Interpolar posición en las coordenadas
      const targetDist = fraccion * totalDist;
      let acum = 0;
      let lng = coords[0][0], lat = coords[0][1];
      for (let i = 1; i < coords.length; i++) {
        const dx = (coords[i][0] - coords[i-1][0]) * 111320 * Math.cos(coords[i][1] * Math.PI / 180);
        const dy = (coords[i][1] - coords[i-1][1]) * 110540;
        const seg = Math.sqrt(dx*dx + dy*dy);
        if (acum + seg >= targetDist) {
          const t = (targetDist - acum) / seg;
          lng = coords[i-1][0] + t * (coords[i][0] - coords[i-1][0]);
          lat = coords[i-1][1] + t * (coords[i][1] - coords[i-1][1]);
          break;
        }
        acum += seg;
      }

      // finalizado ya calculado arriba
      const key = String(resultado.dorsal);
      dorsalesActivos.add(key);

      if (markersRef.current[key]) {
        markersRef.current[key].setLngLat([lng, lat]);
        // Actualizar color si cambió estado
        const el = markersRef.current[key].getElement();
        el.style.background = finalizado ? '#22c55e' : '#d42c19';
      } else {
        const el = crearMarkerEl(resultado.dorsal, finalizado);
        const marker = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
        markersRef.current[key] = marker;
      }
    }

    // Eliminar markers de atletas que ya no están
    Object.keys(markersRef.current).forEach(key => {
      if (!dorsalesActivos.has(key)) {
        markersRef.current[key].remove();
        delete markersRef.current[key];
      }
    });
  }, []);

  // Sync refs
  useEffect(() => { stateRef.current.competicion = competicion; }, [competicion]);
  useEffect(() => { stateRef.current.controles = controles; }, [controles]);
  useEffect(() => { stateRef.current.sectores = sectores; }, [sectores]);
  useEffect(() => { stateRef.current.allResultados = allResultados; calcularPosiciones(); }, [allResultados, calcularPosiciones]);
  useEffect(() => { stateRef.current.lineCoords = lineCoords; calcularPosiciones(); }, [lineCoords, calcularPosiciones]);

  // Fetch init
  useEffect(() => {
    if (!params.competicionId) return;
    fetch(`${CONFIG.API_URL}/api/1.0/live/${params.competicionId}/init/`)
      .then(r => r.json())
      .then(data => {
        const comp = data.competicion;
        setCompeticion(comp);
        setEvento(data.evento);
        setSectores(data.sectores || []);
        setControles(data.controles || []);
        if (comp.mapa_geojson) {
          try {
            const geoJson = typeof comp.mapa_geojson === 'string' ? JSON.parse(comp.mapa_geojson) : comp.mapa_geojson;
            setLineCoords(geoJson.features[0].geometry.coordinates);
          } catch (e) { console.error('mapa_geojson parse error:', e); }
        }
      })
      .catch(err => console.error('live-init error:', err));
  }, [params.competicionId]);

  // WebSocket
  useEffect(() => {
    if (!params.competicionId) return;
    const conectar = () => {
      const ws = new WebSocket(`${CONFIG.WS_URL}/ws/${params.competicionId}`);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.tipo === 'update') {
          const ctrl = data.controles || [];
          const newResultados = enrichResultados((data.resultados || []).map(r => ({ ...r, ...r.controles })), ctrl);
          setAllResultados(newResultados);
          if (ctrl.length) setControles(ctrl);
        } else if (data.tipo === 'delta') {
          const ctrl = data.controles || [];
          setAllResultados(prev => enrichResultados(applyDelta(prev, data.resultados || [], data.eliminados || []), ctrl));
          if (ctrl.length) setControles(ctrl);
        }
      };
      ws.onclose = () => { wsReconnectRef.current = setTimeout(conectar, 3000); };
    };
    conectar();
    intervalRef.current = setInterval(calcularPosiciones, 10000);
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [params.competicionId, calcularPosiciones]);

  return (
    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}>
      <TopHeaderMenu onClickBack={onClickBack} competicion={competicion} evento={evento} />
      <CompeticionHeaderMenu competicion={competicion} selected={5} evento={evento} />
      <div ref={mapContainer} style={{ position: 'absolute', top: '176px', left: 0, right: 0, bottom: 0 }} />
    </div>
  );
}

export default CompeticionGps;
